/* eslint-disable @typescript-eslint/restrict-template-expressions */
import React, { type PropsWithChildren, useContext, useRef, useEffect, useState, useMemo } from "react";
import { twMerge } from "tailwind-merge";

import { Context } from "../context";
import { toPixels } from "../../../../utils/css";

interface AccordionDetailsProps {
  className?: string;
  wrapperClassName?: string;
  unmountOnExit?: boolean;
  animationDuration?: number;
}

const COLORS = {
  primary: "[&.active]:bg-primary-25 text-neutral-800",
  success: "bg-success-25 text-success-900",
  error: "bg-error-25 text-error-900",
  info: "bg-info-25 text-info-900",
  warning: "bg-warning-25 text-warning-600",
};

export function SupAccordionDetails({ unmountOnExit = false, animationDuration = 200, className, wrapperClassName, children }: PropsWithChildren<AccordionDetailsProps>) {
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { variant, expanded } = useContext(Context);
  const [contentVisible, setContentVisibility] = useState(expanded);

  const height = useMemo(() => {
    return expanded ? `var(--accordion-details-height, 0px)` : "0px";
  }, [expanded]);
  const observer = useMemo(
    () =>
      new ResizeObserver(entries => {
        const [entry] = entries;

        containerRef.current?.style.setProperty("--accordion-details-height", toPixels(entry?.target.clientHeight) ?? "0px");
      }),
    []
  );

  const contentUnmounted = !contentVisible && unmountOnExit;

  useEffect(
    function changeContentVisibility() {
      if (expanded === contentVisible) {
        return;
      }

      if (expanded) {
        setContentVisibility(true);
        return;
      } else {
        const timer = setTimeout(() => {
          setContentVisibility(false);
        }, animationDuration);

        return () => {
          clearTimeout(timer);
        };
      }
    },
    [expanded, contentVisible, animationDuration]
  );

  useEffect(
    function updateAccordionHeightOnContentChange() {
      const { current: content } = contentRef;

      if (contentUnmounted || !content) return;

      observer.observe(content);

      return () => {
        observer.unobserve(content);
      };
    },
    [contentUnmounted, observer]
  );

  return (
    <div
      ref={containerRef}
      className={twMerge(
        "sup-accordion-details transition-all overflow-hidden",
        contentVisible ? "visible" : "invisible",
        variant && COLORS[variant],
        contentVisible && "active",
        wrapperClassName
      )}
      style={{ height, transitionDuration: `${animationDuration}ms` }}
    >
      {!contentUnmounted ? (
        <div ref={contentRef} className={twMerge("sup-accordion-details-content flex w-full", className)}>
          {children}
        </div>
      ) : null}
    </div>
  );
}
